import React, { useState } from 'react';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import BasicLayout from 'layout/Basic';
import { useRouter } from 'next/router';
import { Row, Divider, Col, Select, List } from 'antd';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { convertProps } from 'lib/parseAPIResponse';
import { HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import { SectionTitle } from 'components/Section';
import Newsletter from 'containers/Newsletter';
import Subscription from 'containers/Subscription';
import Section from 'components/Section';
import Issue from 'components/IssueItem';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import OurIssueSection from 'components/OurIssueSection';
import EventsBlock from 'components/EventsBlock';
import GalleryBlock from 'components/GalleryBlock';
import VideoBlock from 'components/VideoBlock';

const { Option } = Select;

interface Props {
    issues: Issue[];
    latestIssue: Issue;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    recentArticles: Article[];
    singleVideo: Video;
    singleGallery: Gallery;
    ads: PageAds;
}

export const IssuesPage = (props: Props): React.JSX.Element => {
    const startYear = 2007;
    const currentYear = new Date().getFullYear();

    const years: number[] = [];

    for (let i = currentYear; i >= startYear; i--) {
        years.push(i);
    }

    const screens = useBreakpoint();
    const router = useRouter();

    const [year, setYear] = useState<string>(
        (router.query?.year as string) ?? currentYear.toString()
    );
    const { issues, latestIssue, eventsPatronate, events, singleGallery, singleVideo } = props;

    const handleChangeYear = (newYear: string) => {
        if (newYear !== year) {
            router.push(`?year=${newYear}`);
            setYear(newYear);
        }
    };

    const { ads } = props;
    const { skyscraper, wideboardBottom } = ads;
    return (
        <BasicLayout ads={ads}>
            <Row gutter={[16, 32]}>
                <Content>
                    <SectionTitle title="Archiwum numerów" />
                    <Row justify="end" style={{ margin: '20px 0' }}>
                        <Col xs={16} sm={12} md={8}>
                            <Select
                                size="large"
                                style={{ width: '100%', fontSize: 18 }}
                                value={year}
                                onChange={handleChangeYear}
                            >
                                {years.map((year) => (
                                    <Option key={year.toString()} value={year.toString()}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                    <List
                        grid={{
                            gutter: 24,
                            xs: 1,
                            sm: 2,
                            md: 3,
                            lg: 3,
                            xl: 3,
                            xxl: 3
                        }}
                        dataSource={issues}
                        rowKey={({ seoSlug }: Issue): string => seoSlug}
                        renderItem={(issue: Issue, i) => {
                            if (screens.lg === false && i === 0) {
                                return (
                                    <List.Item style={{ margin: '32px 0 32px 0' }}>
                                        <Issue $issue={issue} />
                                        <Row>
                                            <Col xs={24} md={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[0]}
                                                    margin="15px auto 15px auto"
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[1]}
                                                    margin="15px auto 15px auto"
                                                />
                                            </Col>
                                        </Row>
                                    </List.Item>
                                );
                            }
                            return (
                                <List.Item style={{ margin: '32px 0 32px 0' }}>
                                    <Issue $issue={issue} />
                                </List.Item>
                            );
                        }}
                    />
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <EventsBlock events={events} eventsPatronate={eventsPatronate} />
                        </Col>
                    </Row>
                    <Row justify="center" gutter={[16, 16]}>
                        <Col span={24}>
                            <Rectangle id={ads.rectangle[0]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={ads.halfpage[0]} />
                        </Col>
                    </Row>
                    <Section justify="center" gutter={[16, 16]}>
                        {singleGallery && (
                            <Col span={24}>
                                <GalleryBlock gallery={singleGallery} />
                            </Col>
                        )}
                        <Divider style={{ margin: '0px 0 24px 0' }} />
                        {singleVideo && (
                            <>
                                <Col span={24}>
                                    <VideoBlock video={singleVideo} />
                                </Col>
                                <Divider />
                            </>
                        )}
                        <Col span={24}>
                            <OurIssueSection issue={latestIssue} />
                        </Col>
                    </Section>
                </Sider>
            </Row>

            <Divider />
            <Section gutter={[16, 32]} justify="center">
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
            </Section>
            <Divider />
            <Row>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
                <Col span={24}>
                    <Wideboard id={wideboardBottom[0]} />
                </Col>
            </Row>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
    const { apiUrl } = getConfig().publicRuntimeConfig;
    const year = query.year || new Date().getFullYear();

    return Promise.all([
        fetch(`${apiUrl}/public/api/issues?year=${year}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=archiwum`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default IssuesPage;
